import React, { useState } from "react";

/**
 * Avatar color palette - consistent colors for user avatars
 */
const AVATAR_COLORS = [
  "#C6EBC3", // Light Green
  "#F7CD8D", // Peach / Light Orange
  "#B9DEF8", // Light Blue
  "#EBD998", // Pale Yellow
  "#B6B7E0", // Lavender / Light Purple
  "#A8C8A5", // Mint Green
  "#A0CDBA", // Teal Green
  "#ACCEE6", // Sky Blue
  "#E6CFE3", // Blush Lavender
  "#CFE7E1", // Pale Aqua
  "#F2D6C9", // Soft Coral
];

/**
 * Size configurations for the avatar
 */
const SIZE_CONFIG = {
  xs: {
    container: "h-4 w-4",
    text: "text-[8px]",
  },
  sm: {
    container: "h-5 w-5",
    text: "text-[10px]",
  },
  md: {
    container: "h-6 w-6",
    text: "text-xs",
  },
  lg: {
    container: "h-8 w-8",
    text: "text-sm",
  },
  xl: {
    container: "h-10 w-10",
    text: "text-base",
  },
  "2xl": {
    container: "h-16 w-16",
    text: "text-xl",
  },
  "3xl": {
    container: "h-32 w-32",
    text: "text-5xl",
  },
  "4xl": {
    container: "h-40 w-40",
    text: "text-6xl",
  },
} as const;

type AvatarSize = keyof typeof SIZE_CONFIG;

export interface UserAvatarProps {
  /**
   * The user's name - used to generate initials and display name
   */
  name: string;
  /**
   * Optional user ID (e.g., user_uuid) - used to generate consistent avatar color
   * If provided, this takes precedence over name for color generation
   * This ensures avatar colors remain stable even if names change
   * @default undefined
   */
  userId?: string | number;
  /**
   * Size of the avatar
   * @default "sm"
   */
  size?: AvatarSize;
  /**
   * Whether to show the name next to the avatar
   * @default false
   */
  showName?: boolean;
  /**
   * Custom class name for the name text
   */
  nameClassName?: string;
  /**
   * Custom class name for the container (when showName is true)
   */
  className?: string;
  /**
   * Format the displayed name (capitalize first letter)
   * @default true
   */
  formatName?: boolean;
  /**
   * Optional image URL for the avatar
   * Falls back to initials if URL fails to load or is not provided
   */
  imageUrl?: string;
}

/**
 * Get initials from a name string
 * Takes the first letter of each word (up to 2 letters)
 * Returns "?" if empty
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim() === "") return "?";

  const initials = name
    .trim()
    .split(" ")
    .map((n) => n[0]?.toUpperCase())
    .join("")
    .slice(0, 2);

  return initials || "?";
};

/**
 * Generate a deterministic color based on a seed string
 * Same seed will always produce the same color
 */
export const getAvatarColor = (seed?: string | number): string => {
  const str = String(seed ?? "");
  if (!str) return AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colorIndex = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
};

/**
 * Format a name string (capitalize first letter of each word)
 */
const formatDisplayName = (name: string): string => {
  if (!name || name.trim() === "") return "Unknown";
  return name
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * UserAvatar - A reusable avatar component with consistent styling
 *
 * @example
 * ```tsx
 * // Basic usage - avatar only
 * <UserAvatar name="John Doe" />
 *
 * // With image URL
 * <UserAvatar name="John Doe" imageUrl="https://example.com/avatar.jpg" />
 *
 * // With name displayed
 * <UserAvatar name="John Doe" showName />
 *
 * // Different sizes
 * <UserAvatar name="John Doe" size="lg" />
 *
 * // With formatted name
 * <UserAvatar name="JOHN DOE" showName formatName />
 *
 * // Image with fallback - if image fails, shows initials
 * <UserAvatar name="John Doe" imageUrl="invalid-url" />
 * ```
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  userId,
  size = "sm",
  showName = false,
  nameClassName,
  className,
  formatName = true, // Default to true - always capitalize names for consistency
  imageUrl,
}) => {
  const [imageLoadError, setImageLoadError] = useState(false);

  const sizeConfig = SIZE_CONFIG[size];
  // Use userId for color if available (stable identifier), otherwise fall back to name
  // This ensures avatar colors remain consistent even if names change format/casing
  const backgroundColor = getAvatarColor(userId || name);
  const initial = getInitials(name);
  const displayName = formatName ? formatDisplayName(name) : (name?.trim() || "Internal");

  // Determine if we should show the image or fallback to initials
  const shouldShowImage = imageUrl && !imageLoadError;

  const avatarElement = shouldShowImage ? (
    <img
      src={imageUrl}
      alt={name}
      className={`${sizeConfig.container} rounded-full object-cover flex-shrink-0`}
      onError={() => setImageLoadError(true)}
    />
  ) : (
    <div
      className={`${sizeConfig.container} rounded-full flex items-center justify-center font-medium text-black flex-shrink-0`}
      style={{ backgroundColor }}
    >
      <span className={sizeConfig.text}>{initial}</span>
    </div>
  );

  if (!showName) {
    return avatarElement;
  }

  return (
    <div className={`flex items-center gap-2 ${className ?? ""}`}>
      {avatarElement}
      <span className={nameClassName ?? "text-sm font-medium text-gray-900"}>
        {displayName}
      </span>
    </div>
  );
};